// src/components/ContactRoom.js
//
// 会員 ↔ 運営 のメッセージ画面（管理室）。
//
// 【設計の要点】
//   - 相手は運営だけなので、一覧画面は不要。自分の1部屋を直接開く。
//   - 運営の返信は誰が書いても「運営」と表示する（operatorId は保存するが見せない）。
//   - 部屋は「最初の送信時」に作る。開いただけでは作らない
//     （開いただけの空の部屋が609件できると、管理画面の一覧が汚れるため）。
//
// 【コスト対策】
//   - メッセージは新しい順に limit(30) だけ購読する。
//     limit を付けないと、やり取りが増えるほど開くたびに読み取りが増える。
//   - 「もっと見る」で 30 件ずつ追加する。
//   - 送信時に getDoc で存在確認せず、setDoc(merge) の1回で済ませる。
//   - onSnapshot は useEffect の戻り値で必ず解除する。
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import {
    collection, doc, setDoc, addDoc, onSnapshot, query,
    orderBy, limit, serverTimestamp, updateDoc, increment
} from 'firebase/firestore';
import { COLORS } from '../theme';

const PAGE_SIZE = 30;

// 表示用の日時。serverTimestamp は反映まで一瞬 null になるので保護する。
const fmtTime = (ts) => {
    if (!ts || typeof ts.toDate !== 'function') return '';
    const d = ts.toDate();
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return sameDay ? hm : `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
};

const ContactRoom = ({ user, setActiveTab, appConfig = {} }) => {
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [hasMore, setHasMore] = useState(false);
    // 購読が失敗したとき（部屋が未作成など）に、送信後に張り直すための鍵。
    //   onSnapshot は一度エラーで止まると自動では復活しないため。
    const [reloadKey, setReloadKey] = useState(0);
    const subFailed = useRef(false);
    const endRef = useRef(null);
    const didScroll = useRef(false);

    const userId = user?.userId;
    const roomId = userId ? `admin_${userId}` : null;

    // 受信の購読（新しい順に limit 件だけ）
    useEffect(() => {
        if (!roomId) return;
        const q = query(
            collection(db, 'chats', roomId, 'messages'),
            orderBy('createdAt', 'desc'),
            limit(pageSize)
        );
        const unsub = onSnapshot(q, (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            list.reverse();                    // 表示は古い順
            setMessages(list);
            setHasMore(snap.docs.length >= pageSize);
            setIsLoading(false);
            if (!didScroll.current) {
                didScroll.current = true;
                setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'auto' }), 120);
            }
        }, (err) => {
            console.error('メッセージ取得エラー:', err);
            subFailed.current = true;
            setIsLoading(false);
        });
        return () => unsub();
    }, [roomId, pageSize, reloadKey]);

    // 開いたら自分の未読を0にする（部屋がまだ無いときは何もしない）
    useEffect(() => {
        if (!roomId || !userId || isLoading || messages.length === 0) return;
        updateDoc(doc(db, 'chats', roomId), { [`unread.${userId}`]: 0 })
            .catch(() => { /* 部屋が未作成なら無視してよい */ });
    }, [roomId, userId, isLoading, messages.length]);

    const handleSend = async () => {
        const text = inputText.trim();
        if (!text || isSending || !roomId) return;
        setIsSending(true);
        setInputText('');   // 先にクリアして体感速度を上げる

        try {
            // ルーム情報。存在確認せず merge で1回に集約する。
            // ★ unread は「入れ子のマップ」として書く。
            //   setDoc はドット記法をフィールドパスとして解釈しないため、
            //   { 'unread.admin': 1 } と書くと "unread.admin" という名前の
            //   フィールドが1つできてしまい、未読バッジが動かなくなる。
            //   （ドット記法が使えるのは updateDoc のみ）
            await setDoc(doc(db, 'chats', roomId), {
                type: 'admin',
                members: ['admin', userId],
                memberNames: { [userId]: user?.displayName || '' },
                lastMessage: text.slice(0, 60),
                lastSenderId: userId,
                updatedAt: serverTimestamp(),
                unread: {
                    admin: increment(1),   // 運営側の未読を増やす
                    [userId]: 0            // 自分の未読は0に
                }
            }, { merge: true });

            await addDoc(collection(db, 'chats', roomId, 'messages'), {
                senderId: userId,
                text,
                createdAt: serverTimestamp()
            });

            // 初回送信前に購読が失敗していたら、ここで張り直す。
            if (subFailed.current) {
                subFailed.current = false;
                setReloadKey(k => k + 1);
            }
            setTimeout(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), 120);
        } catch (e) {
            console.error('送信エラー:', e);
            alert('送信できませんでした。通信環境をご確認のうえ、もう一度お試しください。');
            setInputText(text);   // 失敗したら入力内容を戻す
        } finally {
            setIsSending(false);
        }
    };

    if (!userId) {
        return (
            <div className="container" style={{ paddingTop: '40px', textAlign: 'center', color: '#999' }}>
                ログイン情報が取得できませんでした。<br />アプリを開き直してください。
            </div>
        );
    }

    const replyNote = appConfig.contactReplyNote || '運営が確認しだい返信します（通常3営業日以内）';

    return (
        <div className="container" style={{ paddingTop: '15px', paddingBottom: '180px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h2 style={{ margin: 0, fontSize: '19px', color: '#333' }}>💬 運営への問い合わせ</h2>
                <button
                    onClick={() => setActiveTab('profile')}
                    className="btn"
                    style={{ background: 'white', border: '1px solid #ddd', fontSize: '12px', padding: '6px 12px' }}
                >
                    ← 戻る
                </button>
            </div>

            <div style={{
                background: '#e8f4fd', border: '1px solid #b3d9f2', borderRadius: '8px',
                padding: '12px', marginBottom: '15px', fontSize: '12px', color: '#0d47a1', lineHeight: '1.7'
            }}>
                アプリの使い方・イベント・ポイントのことなど、お気軽にご相談ください。
                <span style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: '#1565c0' }}>
                    {replyNote}
                </span>
            </div>

            {/* --- メッセージ --- */}
            <div style={{
                background: '#7fb5b5', borderRadius: '12px', padding: '15px',
                minHeight: '300px', display: 'flex', flexDirection: 'column', gap: '10px'
            }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', color: 'white', fontSize: '12px', padding: '40px 0' }}>読み込み中...</div>
                ) : messages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: 'white', fontSize: '12px', padding: '40px 10px', lineHeight: '1.8' }}>
                        まだメッセージはありません。<br />
                        下の入力欄からお送りください。
                    </div>
                ) : (
                    <>
                        {hasMore && (
                            <button
                                onClick={() => setPageSize(p => p + PAGE_SIZE)}
                                style={{
                                    background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '20px',
                                    padding: '6px 14px', fontSize: '11px', color: '#555', cursor: 'pointer',
                                    alignSelf: 'center', marginBottom: '4px'
                                }}
                            >
                                以前のメッセージを表示
                            </button>
                        )}

                        {messages.map(msg => {
                            const isMe = msg.senderId === userId;
                            return (
                                <div key={msg.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: '6px' }}>
                                    {!isMe && (
                                        <div style={{
                                            width: '30px', height: '30px', borderRadius: '50%', background: '#fff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '14px', border: '1px solid #ccc', flexShrink: 0
                                        }}>🛡️</div>
                                    )}
                                    <div style={{ maxWidth: '72%' }}>
                                        {/* ★運営の返信は誰が書いても「運営」と表示する */}
                                        {!isMe && (
                                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.9)', marginBottom: '2px' }}>運営</div>
                                        )}
                                        <div style={{
                                            padding: '9px 12px',
                                            borderRadius: isMe ? '15px 15px 0 15px' : '15px 15px 15px 0',
                                            background: isMe ? '#85e249' : '#fff',
                                            color: '#333', fontSize: '14px', wordBreak: 'break-word',
                                            whiteSpace: 'pre-wrap', boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                        }}>
                                            {msg.text}
                                        </div>
                                        <div style={{
                                            fontSize: '9px', color: 'rgba(255,255,255,0.85)',
                                            marginTop: '2px', textAlign: isMe ? 'right' : 'left'
                                        }}>
                                            {fmtTime(msg.createdAt)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
                <div ref={endRef} />
            </div>

            {/* --- 入力 --- */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <textarea
                    value={inputText}
                    onChange={e => setInputText(e.target.value)}
                    placeholder="メッセージを入力..."
                    rows={2}
                    style={{
                        flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #ccc',
                        fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit'
                    }}
                />
                <button
                    onClick={handleSend}
                    disabled={!inputText.trim() || isSending}
                    style={{
                        background: !inputText.trim() || isSending ? '#ccc' : COLORS.primary,
                        color: 'white', border: 'none', borderRadius: '10px',
                        padding: '0 18px', fontWeight: 'bold', fontSize: '14px',
                        cursor: inputText.trim() && !isSending ? 'pointer' : 'default', flexShrink: 0
                    }}
                >
                    {isSending ? '...' : '送信'}
                </button>
            </div>

            <div style={{ fontSize: '10px', color: '#aaa', marginTop: '8px', lineHeight: '1.7' }}>
                ※ 個人情報やパスワードは送信しないでください。<br />
                ※ 送信した内容は運営が確認します。取り消しはできません。
            </div>
        </div>
    );
};

export default ContactRoom;